import {
  createContext,
  useEffect,
  useMemo,
  useState,
  Fragment,
  isValidElement,
  ReactNode,
} from "react";
import defaultTranslations from "@/translations/en.json";
import { generateTranslations } from "@/utils/translations";
import parse, { Element, domToReact } from "html-react-parser";
import type { DOMNode } from "html-dom-parser";
import type { ChildNode } from "domhandler";
import { parseDocument } from "htmlparser2";
import { ReadMoreContent } from "@/components/ui/ReadMoreContent";
import { IS_DEVELOPMENT, IS_TEST } from "@/config";
import {
  Translations,
  TranslateParams,
  TranslationResult,
  TranslationProviderProps,
  TranslationContextType,
  TranslateOptions,
} from "./TranslationProviderProps";
import DOMPurify from "dompurify";

const showWarnings = IS_DEVELOPMENT && !IS_TEST;

// Mirrors the form builder's Quill toolbar (RichTextField.tsx in the backend).
const SANITIZE_CONFIG = {
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_ATTR: [
    "href",
    "rel",
    "target",
    "class",
    "title",
    "data-readmore",
    "data-list",
  ],
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "a",
    "ol",
    "ul",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "span",
    "div",
    "hr",
  ],
};

export const sanitizeHtmlCopy = (value: string) =>
  DOMPurify.sanitize(value, SANITIZE_CONFIG);

function isReadMoreBreakpoint(node: ChildNode): node is Element {
  return (
    node.type === "tag" &&
    (node as Element).name === "hr" &&
    (node as Element).attribs?.["data-readmore"] === "true"
  );
}

function splitAtReadMoreBreakpoint(
  nodes: ChildNode[],
): [ChildNode[], ChildNode[]] | null {
  const breakpointIndex = nodes.findIndex(isReadMoreBreakpoint);

  if (breakpointIndex === -1) {
    return null;
  }

  const visibleNodes = nodes.slice(0, breakpointIndex);
  const hiddenNodes = nodes.slice(breakpointIndex + 1);

  return [visibleNodes, hiddenNodes];
}

function isEmptyNode(node: ChildNode): boolean {
  if (node.type === "text") {
    const textNode = node as unknown as { data: string };
    return !textNode.data || textNode.data.trim() === "";
  }

  if (node.type === "tag") {
    const element = node as Element;

    if (!element.children || element.children.length === 0) {
      return true;
    }

    return element.children.every((child) => {
      if (child.type === "text") {
        const textChild = child as unknown as { data: string };
        return !textChild.data || textChild.data.trim() === "";
      }
      if (child.type === "tag") {
        const tagChild = child as Element;
        return tagChild.name === "br";
      }
      return false;
    });
  }

  return false;
}

function filterEmptyNodes(nodes: ChildNode[]): ChildNode[] {
  return nodes.filter((node) => !isEmptyNode(node));
}

function parseHtmlWithReadMore(html: string): ReactNode {
  const document = parseDocument(html);
  const nodes = document.children;

  const splitResult = splitAtReadMoreBreakpoint(nodes);

  if (splitResult) {
    const [visibleNodes, hiddenNodes] = splitResult;

    const filteredVisibleNodes = filterEmptyNodes(visibleNodes);
    const filteredHiddenNodes = filterEmptyNodes(hiddenNodes);

    return (
      <ReadMoreContent
        visibleContent={domToReact(filteredVisibleNodes as DOMNode[])}
        hiddenContent={domToReact(filteredHiddenNodes as DOMNode[])}
      />
    );
  }

  return parse(html);
}

export const TranslationContext = createContext<TranslationContextType | null>(
  null,
);

function interpolateParams(
  translation: string,
  params?: TranslateParams,
): string | ReactNode {
  const hasComponents = params
    ? Object.values(params).some((p) => isValidElement(p))
    : false;

  if (!hasComponents) {
    return translation.replace(/{{\s*([^}\s]+)\s*}}/g, (_match, key) => {
      const value = params?.[key];

      if (typeof value === "undefined") {
        if (showWarnings) {
          console.log(`Param "${key}" not found in translation`);
        }
        return "";
      }

      return sanitizeHtmlCopy(String(value));
    });
  }

  const parts = translation.split(/({{\s*[^}\s]+\s*}})/g);

  return (
    <>
      {parts.map((part, index) => {
        const match = part.match(/{{\s*([^}\s]+)\s*}}/);
        if (match) {
          const key = match[1];
          const value = params![key];

          if (typeof value === "undefined") {
            if (showWarnings) {
              console.log(`Param "${key}" not found in translation`);
            }
            return null;
          }

          if (isValidElement(value)) {
            return <Fragment key={index}>{value}</Fragment>;
          }

          if (typeof value === "string") {
            return (
              <span
                key={index}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlCopy(value),
                }}
              />
            );
          }

          return <Fragment key={index}>{value}</Fragment>;
        }

        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}

export const TranslationProvider = ({
  children,
  translations,
}: TranslationProviderProps) => {
  const [mergedTranslations, setMergedTranslations] = useState<Translations>(
    () => generateTranslations(defaultTranslations, translations),
  );

  useEffect(() => {
    const merged = generateTranslations(defaultTranslations, translations);
    setMergedTranslations(merged);
  }, [translations]);

  const t = useMemo(() => {
    return (
      key: string,
      params?: TranslateParams,
      options?: TranslateOptions,
    ): TranslationResult => {
      const translation = mergedTranslations[key];

      if (typeof translation === "undefined") {
        if (showWarnings) {
          console.log(`Translation for key "${key}" not found`);
        }
        return key;
      }

      if (translation === "") {
        return "";
      }

      if (key.endsWith("-html")) {
        if (params && Object.values(params).some((p) => isValidElement(p))) {
          return parse(sanitizeHtmlCopy(translation), {
            replace: (domNode) => {
              if (domNode.type === "text" && domNode.data) {
                const parts = domNode.data.split(/({{\s*[^}\s]+\s*}})/g);
                if (parts.length > 1) {
                  return (
                    <>
                      {parts.map((part, i) => {
                        const match = part.match(/{{\s*([^}\s]+)\s*}}/);
                        if (match) {
                          const pKey = match[1];
                          const val = params[pKey];
                          if (isValidElement(val))
                            return <Fragment key={i}>{val}</Fragment>;
                          if (typeof val === "string")
                            return parse(sanitizeHtmlCopy(val));
                          return val;
                        }
                        return part;
                      })}
                    </>
                  );
                }
              }
            },
          });
        }

        // Sanitised after interpolation: a param can fill an attribute
        // (href="{{link}}"), and only the merged string shows whether it
        // closed the quote and added attributes of its own.
        const safeTranslation = sanitizeHtmlCopy(
          interpolateParams(translation, params) as string,
        );
        const transformedTranslation =
          typeof options?.transform === "function"
            ? options.transform(safeTranslation)
            : safeTranslation;

        return parseHtmlWithReadMore(transformedTranslation);
      }

      const translationWithParams = interpolateParams(translation, params);

      if (typeof translationWithParams === "string") {
        return typeof options?.transform === "function"
          ? options.transform(translationWithParams)
          : translationWithParams;
      }

      return translationWithParams;
    };
  }, [mergedTranslations]);

  const contextValue = useMemo(() => ({ t }), [t]);

  return (
    <TranslationContext value={contextValue}>{children}</TranslationContext>
  );
};
